/**
 * Presence — where the other people in the room are, drawn on a page that is not the same shape as
 * theirs.
 *
 * design.md §7.6. Three renderings, one rule for choosing between them:
 *
 *   · **on your page**    → an initialed dot in the margin, beside the line they are on
 *   · **off your page**   → a tick on the chapter scrubber, at their position on the shared axis
 *   · **far off**         → a sentence: *"Karti is 3 pages ahead"*, computed from `ord`, **not `ms`**
 *
 * ── ⚠️ NO xy CURSOR, AND NO xy STROKES. THIS IS THE ONE THING NOT TO PORT ───
 * movies has a live cursor and freehand strokes in normalised 0..1 coordinates, and they are the best
 * part of that product. They work because **a video frame is size-invariant**: 0.42 of the way across
 * is the same pixel of the same image on a phone and on a projector, so an arrow drawn on one lands
 * on the same actor on the other.
 *
 * **Reflowable text is not size-invariant.** The same sid is at the top of column 3 at 16 px and
 * halfway down column 5 at 22 px; the same 0..1 point is a different *word* on every device in the
 * room, and it is a different word again after a rotation. An xy annotation here does not degrade —
 * it silently means something else, on a screen where meaning is the entire content. So there is no
 * xy anywhere in this file: every position a person has is a **sid**, and a sid is resolved to
 * pixels *locally*, by asking this device's own DOM where it put that sentence.
 *
 * Reactions are `{emoji, sid}` for the same reason, and they are ephemeral (3 s) because a reaction
 * anchored to a sentence that is about to reflow off the page is a note, and notes are a later phase
 * with a fixed anchor type (`{sid, charStart, charEnd}`, §7.6) that is deliberately not this.
 *
 * ── Why the DOM and not a prop ──────────────────────────────────────────────
 * Resolving a sid to a rectangle needs the column map, which is `Paginator`'s and lives inside
 * `Reader.tsx`. This file asks the document instead — `[data-s="…"]`, §5.4's one selector, the same
 * one `useCueSync` uses — because that is a read of a layout that has already happened rather than a
 * second copy of the layout logic. `PresenceHost` makes it injectable, so `node --test` drives every
 * decision here with rectangles it wrote itself and no DOM at all.
 */

import { useMemo, useRef, type CSSProperties, type ReactElement } from "react";

import type { BookPos } from "../../../shared/position.ts";
import { initialsOf, pagesAhead, type RoomMember } from "../sync/room.ts";
import type { FloatingReaction } from "../sync/useRoomSync.ts";
import type { ScrubberTick } from "./Scrubber.tsx";

/* ══════════════════════════════════════════════════════════════════════════
 * Resolving a sentence to a place on this screen
 * ══════════════════════════════════════════════════════════════════════════ */

export interface Rect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface PresenceHost {
  /** The rectangle of `[data-s="sid"]`, in viewport coordinates. Null when it is not mounted. */
  rectOf(sid: string): Rect | null;
  /** The reader frame's rectangle — the visible column, which is what "on your page" means. */
  frameRect(): Rect | null;
}

/**
 * The browser host. One `querySelector` per member per relayout, which is a handful per minute.
 *
 * `CSS.escape` for the same reason `useCueSync` gives: a sid is `c003.b0042.s1`, the dots are
 * attribute-value content rather than selector syntax, and escaping is what keeps this correct on the
 * day a sid grows a character where that stops being true.
 */
export function domPresenceHost(track: HTMLElement | null, frame: HTMLElement | null): PresenceHost {
  return {
    rectOf(sid) {
      if (!track) return null;
      const el = track.querySelector<HTMLElement>(`[data-s="${escapeAttr(sid)}"]`);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
    },
    frameRect() {
      if (!frame) return null;
      const r = frame.getBoundingClientRect();
      return { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
    },
  };
}

function escapeAttr(v: string): string {
  return typeof CSS !== "undefined" && typeof CSS.escape === "function"
    ? CSS.escape(v)
    : v.replace(/["\\]/g, "\\$&");
}

/**
 * Is this sentence on the page the reader is actually looking at?
 *
 * ⚠️ **Mounted is not visible.** The whole chapter part is in the DOM; only one column of it is
 * inside the frame, and the rest is translated off to the sides by `--frame-h`-sized amounts. A
 * presence check that only asked "does `[data-s]` exist" would put every member's dot in the margin
 * of every page of the chapter — which is worse than showing none, because it is confidently wrong
 * about the one thing this component exists to say.
 *
 * Horizontal containment is what decides it (the track translates horizontally, §5.1) and the
 * vertical test is a courtesy for a spread that is taller than the frame. One pixel of tolerance,
 * because a column boundary lands on a subpixel and a sentence exactly on the seam should read as
 * on-screen rather than flicker.
 */
export function sidOnScreen(host: PresenceHost, sid: string): boolean {
  const frame = host.frameRect();
  const r = host.rectOf(sid);
  if (frame === null || r === null) return false;
  if (r.right < frame.left + 1 || r.left > frame.right - 1) return false;
  if (r.bottom < frame.top + 1 || r.top > frame.bottom - 1) return false;
  return true;
}

/* ══════════════════════════════════════════════════════════════════════════
 * Deciding what each member gets
 * ══════════════════════════════════════════════════════════════════════════ */

export type PresenceRendering =
  | { kind: "margin"; member: RoomMember; sid: string; top: number }
  | { kind: "tick"; member: RoomMember; ms: number }
  /** `pages` is null when the ordinals are ordered but not comparable in magnitude — `pagesAhead`. */
  | { kind: "afar"; member: RoomMember; pages: number | null };

export interface PresenceInput {
  members: readonly RoomMember[];
  /** Us. We are never drawn — a dot in your own margin is furniture. */
  myKey: string | null;
  myPos: BookPos | null;
  /**
   * `sentenceCount / pageCount` for this edition — see `pagesAhead`. **Pass 0 when this client's
   * ordinals are derived rather than the server's**, which is what makes the copy say "elsewhere in
   * the book" instead of a confident and meaningless number.
   */
  sentencesPerPage: number;
  /** Milliseconds of this chapter that precede the current page's artifact, for the ticks. */
  chapterOffsetMs: number;
  chapterDurationMs: number;
}

/**
 * One pass over the members, producing exactly one rendering each.
 *
 * A member gets a **margin dot** when their sentence is on screen; a **tick** when it is not but
 * their position is expressible on the chapter axis this reader is showing; and the **sentence**
 * otherwise. They are mutually exclusive on purpose — the same person in the margin *and* on the
 * scrubber *and* in a line of copy is three widgets reporting one fact, and a reader learns to
 * ignore all three.
 *
 * ⚠️ The tick's `ms` is the member's own `at.ms`, which came off the wire on the room's **pinned**
 * timeline (§7.3). That is legitimate for a *tick* — a mark on a rail is advisory, which is precisely
 * what LAW 3 permits `ms` to be — and it is illegitimate for anything that seeks. Nothing here seeks.
 */
export function presenceRenderings(
  input: PresenceInput,
  host: PresenceHost,
): PresenceRendering[] {
  const out: PresenceRendering[] = [];
  const frame = host.frameRect();

  for (const m of input.members) {
    if (m.key === input.myKey) continue;
    // §7.6: membership is durable and a disconnect does not remove it, so a sleeping laptop stays in
    // the member list — but its owner is not *here*, and drawing their dot beside a line they cannot
    // see is the room lying about who is reading with you.
    if (!m.connected) continue;
    const at = m.at;
    if (at === null) continue;

    const r = host.rectOf(at.sid);
    if (r !== null && frame !== null && sidOnScreen(host, at.sid)) {
      out.push({ kind: "margin", member: m, sid: at.sid, top: r.top - frame.top });
      continue;
    }

    const inChapter = at.ms - input.chapterOffsetMs;
    if (input.chapterDurationMs > 0 && inChapter >= 0 && inChapter <= input.chapterDurationMs) {
      out.push({ kind: "tick", member: m, ms: inChapter });
      continue;
    }

    const pages = input.myPos === null ? null : pagesAhead(at, input.myPos, input.sentencesPerPage);
    out.push({ kind: "afar", member: m, pages });
  }
  return out;
}

/** The `ScrubberTick[]` half, ready for `Reader.tsx`'s `<Scrubber ticks=…>` seam. */
export function scrubberTicks(renderings: readonly PresenceRendering[]): ScrubberTick[] {
  return renderings
    .filter((r): r is Extract<PresenceRendering, { kind: "tick" }> => r.kind === "tick")
    .map((r) => ({ id: r.member.key, ms: r.ms, color: r.member.color, label: r.member.name }));
}

/**
 * *"Karti is 3 pages ahead."* — §7.6, and **`ord`, not `ms`**.
 *
 * The plural is spelled out rather than templated with an `s` because "1 pages behind" is the kind of
 * thing that survives review and then sits on a screen for a year. `0` is "on your page" and not "0
 * pages ahead", which is a sentence nobody has ever said out loud.
 *
 * `null` — ordinals ordered but not comparable in magnitude — gets a sentence that is **less
 * specific and still true**, rather than a number that is specific and wrong. See `pagesAhead`.
 */
export function aheadCopy(name: string, pages: number | null): string {
  if (pages === null) return `${name} is elsewhere in the book`;
  if (pages === 0) return `${name} is about where you are`;
  const n = Math.abs(pages);
  const unit = n === 1 ? "page" : "pages";
  return `${name} is ${n} ${unit} ${pages > 0 ? "ahead" : "behind"}`;
}

/* ══════════════════════════════════════════════════════════════════════════
 * The rendering
 * ══════════════════════════════════════════════════════════════════════════ */

const dotStyle = (color: string, top: number): CSSProperties => ({
  position: "absolute",
  top: `${top}px`,
  left: 0,
  transform: "translateY(-2px)",
  width: "1.35rem",
  height: "1.35rem",
  borderRadius: "var(--radius-sm)",
  background: color,
  color: "var(--ink-inverse)",
  fontFamily: "var(--font-ui)",
  fontSize: "0.625rem",
  fontWeight: 600,
  letterSpacing: "0.02em",
  display: "grid",
  placeItems: "center",
  // The dot is information, never a target. Pointer events off so it can never eat a tap meant for
  // the page-turn zone underneath it — a margin is exactly where a thumb lands.
  pointerEvents: "none",
  // 180 ms, so a dot that moves with a page turn reads as the same person moving rather than as two
  // dots. Not longer: it must have finished before the next sentence lands at 2/s.
  transition: "top 180ms ease-out",
  boxShadow: "var(--shadow-hair)",
});

export interface MarginDotsProps {
  renderings: readonly PresenceRendering[];
}

/**
 * The dots, positioned against the reader frame.
 *
 * Absolutely positioned inside a container the caller places over the margin. `aria-hidden`: the
 * same information is in `<PresenceLine>` as a sentence, and a screen reader announcing "K" beside
 * every third paragraph is noise where the sentence is the signal.
 */
export function MarginDots({ renderings }: MarginDotsProps): ReactElement | null {
  const dots = renderings.filter(
    (r): r is Extract<PresenceRendering, { kind: "margin" }> => r.kind === "margin",
  );
  if (dots.length === 0) return null;
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }} aria-hidden="true">
      {dots.map((d) => (
        <span key={d.member.key} style={dotStyle(d.member.color, d.top)} title={d.member.name}>
          {initialsOf(d.member.name)}
        </span>
      ))}
    </div>
  );
}

export interface PresenceLineProps {
  renderings: readonly PresenceRendering[];
  /** Tapping a person takes you to them. Local only — it never moves them (§7.1's `resync` shape). */
  onGoTo?: ((sid: string) => void) | null;
}

/**
 * The sentence half. One line per member who is not on your page.
 *
 * `aria-live="polite"`, never assertive: this changes when somebody else turns a page, and
 * interrupting the sentence a screen-reader user is in the middle of to say where a *different*
 * person is reading is the opposite of what "together" is supposed to feel like. `FollowPill` makes
 * the same call for the same reason.
 */
export function PresenceLine({ renderings, onGoTo }: PresenceLineProps): ReactElement | null {
  const afar = renderings.filter((r) => r.kind !== "margin");
  if (afar.length === 0) return null;
  return (
    <div
      aria-live="polite"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "var(--space-2)",
        fontFamily: "var(--font-ui)",
        fontSize: "0.75rem",
        color: "var(--ink-muted)",
      }}
    >
      {afar.map((r) => {
        const sid = r.member.at?.sid ?? null;
        const text =
          r.kind === "afar" ? aheadCopy(r.member.name, r.pages) : `${r.member.name} is elsewhere in this chapter`;
        return (
          <button
            key={r.member.key}
            type="button"
            disabled={sid === null || !onGoTo}
            onClick={() => (sid !== null ? onGoTo?.(sid) : undefined)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "var(--space-1)",
              padding: "0.125rem var(--space-2)",
              border: "1px solid var(--rule)",
              borderRadius: "var(--radius-pill)",
              background: "transparent",
              color: "inherit",
              font: "inherit",
              cursor: sid !== null && onGoTo ? "pointer" : "default",
            }}
            // "Go to" and not "follow": this turns THIS reader's page and emits nothing. §7.1's
            // `resync()` is local-only and so is this; a control in a room that silently moved four
            // other people would be the single most alarming button in the product.
            title={sid !== null ? `Go to where ${r.member.name} is reading` : undefined}
          >
            <span
              aria-hidden="true"
              style={{
                width: "0.5rem",
                height: "0.5rem",
                borderRadius: "var(--radius-sm)",
                background: r.member.color,
              }}
            />
            {text}
          </button>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * Reactions
 * ══════════════════════════════════════════════════════════════════════════ */

export interface ReactionsProps {
  reactions: readonly FloatingReaction[];
  host: PresenceHost;
  /** Bumped by the caller whenever the layout moved, so anchors are re-resolved. */
  layoutNonce?: number;
}

/**
 * Emoji, anchored to the sentence they were sent about.
 *
 * A reaction whose sentence is not on this screen is **not drawn**. movies floats every reaction up
 * the side of the video because every viewer is looking at the same frame; here the sender was
 * reacting to a *sentence*, and floating it over a paragraph three pages away attaches somebody's
 * "!" to text they never read. Dropping it is the honest failure: the reaction was about something
 * this reader is not looking at.
 */
export function Reactions({ reactions, host, layoutNonce = 0 }: ReactionsProps): ReactElement | null {
  // `layoutNonce` is a real dependency and not decoration: the rectangles below are imperative reads
  // of a layout React cannot observe, so the nonce is the caller telling us they moved.
  const placed = useMemo(() => {
    const frame = host.frameRect();
    const out: { r: FloatingReaction; top: number; left: number }[] = [];
    for (const r of reactions) {
      if (r.sid === null) continue;
      const rect = host.rectOf(r.sid);
      if (rect === null || frame === null || !sidOnScreen(host, r.sid)) continue;
      out.push({ r, top: rect.top - frame.top, left: Math.max(0, rect.left - frame.left) });
    }
    return out;
  }, [reactions, host, layoutNonce]);

  if (placed.length === 0) return null;
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }} aria-hidden="true">
      {placed.map(({ r, top, left }) => (
        <span
          key={r.id}
          style={{
            position: "absolute",
            top: `${top}px`,
            left: `${left}px`,
            fontSize: "1.25rem",
            lineHeight: 1,
            filter: "drop-shadow(0 1px 1px rgb(58 42 24 / 25%))",
          }}
          title={`${r.name} reacted`}
        >
          {r.emoji}
        </span>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * The hook that keeps the above honest across a relayout
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Recompute the renderings when the layout could have moved.
 *
 * A rectangle read is a snapshot, and every one of the things this reader does routinely — a page
 * turn, a font-size change, a rotation, a chapter load — invalidates all of them at once with no
 * event React can see. So the caller bumps `layoutNonce` (from `onTurned`, from the repaginate
 * landing) and this recomputes.
 *
 * It does **not** poll. A `setInterval` reading `getBoundingClientRect` for four members is a forced
 * layout several times a second on a device that is also decoding audio, and §13.1's whole falsifier
 * is layout cost on a real phone. Presence changing 250 ms late after a rotation is invisible;
 * a reflow four times a second is not.
 */
export function usePresence(input: PresenceInput, host: PresenceHost, layoutNonce: number): PresenceRendering[] {
  // Both the caller's `input` object and the `host` are rebuilt on every render — the first because
  // `members` is a new array on every `{t:"at"}`, the second because it closes over two element refs
  // — so memoising on *them* would memoise nothing. The real inputs are: who is where (`positions`),
  // whether the layout moved (`layoutNonce`), and the four scalars the arithmetic reads.
  const positions = input.members.map((m) => `${m.key}:${m.at?.sid ?? ""}:${m.connected ? 1 : 0}`).join("|");

  const latest = useRef({ input, host });
  latest.current = { input, host };

  return useMemo(
    () => presenceRenderings(latest.current.input, latest.current.host),
    [
      positions,
      layoutNonce,
      input.myKey,
      input.myPos?.ord,
      input.sentencesPerPage,
      input.chapterOffsetMs,
      input.chapterDurationMs,
    ],
  );
}
